import {
  REPLAY_PLAN_COPY_JPEG_QUALITY,
  REPLAY_PLAN_COPY_LONGEST_SIDE_PX,
} from "../constants";
import type { CreateReplayPlanCopy, CreateReplayPlanCopyArgs } from "./types";

// Each copy briefly holds the full decoded plan in memory, so copies never run at the same time
let previousCopyFinished: Promise<void> = Promise.resolve();

const drawOnWhiteCanvasAndRelease = (
  bitmap: ImageBitmap,
): HTMLCanvasElement | null => {
  try {
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const context = canvas.getContext("2d");
    if (context === null) return null;

    // JPEG has no alpha, so transparent pixels would otherwise turn black
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, bitmap.width, bitmap.height);
    context.drawImage(bitmap, 0, 0);
    return canvas;
  } finally {
    bitmap.close();
  }
};

const encodeCanvasAsJpeg = (canvas: HTMLCanvasElement): Promise<Blob | null> =>
  new Promise((resolve) => {
    canvas.toBlob(resolve, "image/jpeg", REPLAY_PLAN_COPY_JPEG_QUALITY);
  });

const readBlobAsDataUrl = (blob: Blob): Promise<string | null> =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(typeof reader.result === "string" ? reader.result : null);
    };
    reader.onerror = () => {
      resolve(null);
    };
    reader.readAsDataURL(blob);
  });

const makeReplayPlanCopy = async ({
  sourceUrl,
  naturalWidth,
  naturalHeight,
  signal,
}: CreateReplayPlanCopyArgs): Promise<string | null> => {
  if (signal.aborted) return null;
  if (sourceUrl === "" || naturalWidth <= 0 || naturalHeight <= 0) return null;

  const copyScale = Math.min(
    1,
    REPLAY_PLAN_COPY_LONGEST_SIDE_PX / Math.max(naturalWidth, naturalHeight),
  );
  const copyWidth = Math.max(1, Math.round(naturalWidth * copyScale));
  const copyHeight = Math.max(1, Math.round(naturalHeight * copyScale));

  try {
    // force-cache reuses the bytes the plan image just downloaded
    const response = await fetch(sourceUrl, { cache: "force-cache", signal });
    if (!response.ok) return null;

    const sourceBlob = await response.blob();
    if (signal.aborted) return null;

    // A blob source lets Chrome decode off the main thread; an <img> source freezes the page
    // NOTE: try a web worker later; the resize still holds the main thread for ~40 ms
    const bitmap = await createImageBitmap(sourceBlob, {
      resizeWidth: copyWidth,
      resizeHeight: copyHeight,
      resizeQuality: "medium",
    });

    // A browser that ignored the resize would make the draw scale the full plan on the main thread
    const isResized = bitmap.width === copyWidth;
    if (signal.aborted || !isResized) {
      bitmap.close();
      return null;
    }

    const canvas = drawOnWhiteCanvasAndRelease(bitmap);
    if (canvas === null) return null;

    const jpegBlob = await encodeCanvasAsJpeg(canvas);
    if (jpegBlob === null || signal.aborted) return null;

    const dataUrl = await readBlobAsDataUrl(jpegBlob);
    return dataUrl;
  } catch {
    return null;
  }
};

const makeCopyAfterPrevious = async (
  previousCopy: Promise<void>,
  args: CreateReplayPlanCopyArgs,
): Promise<string | null> => {
  await previousCopy;
  const dataUrl = await makeReplayPlanCopy(args);
  return dataUrl;
};

// Resolves without the data URL and never rejects, so the queue holds no copy and cannot get stuck
const waitUntilFinished = async (
  copy: Promise<string | null>,
): Promise<void> => {
  try {
    await copy;
  } catch {
    return;
  }
};

export const createReplayPlanCopy: CreateReplayPlanCopy = (args) => {
  const queuedCopy = makeCopyAfterPrevious(previousCopyFinished, args);
  previousCopyFinished = waitUntilFinished(queuedCopy);
  return queuedCopy;
};
